import {DisplayObject, Sprite} from 'pixi.js';
import {CardData, ICardData} from '../../../data/active_data/field_config/card_stack_data/CardData';
import {Card} from '../game_table/table_objects/stacks/Card';
import {ArrayWrapEvent} from '../../../data/active_data/base/static_config_data/array_wrap/ArrayWrapEvent';
import {ArrayWrapEventData} from '../../../data/active_data/base/static_config_data/array_wrap/ArrayWrapEventData';
import {ArrayWrap} from '../../../data/active_data/base/static_config_data/ArrayWrap';
import {CardsHolderEvents} from './cards_holder/CardsHolderEvents';
import {HandEvents} from './HandEvents';
import {ActiveData} from '../../../data/ActiveData';
import {CardEvents} from '../game_table/table_objects/stacks/card/CardEvents';
import {CardSelector} from '../../../controllers/CardSelector';
import PixiUtil from '../../../../api/render/pixi/PixiUtil';
import {DynamicData} from '../../../data/DynamicData';
import {StackSelector} from '../../../controllers/StackSelector';
import {Stack} from '../game_table/table_objects/stacks/Stack';

export class HandCardsHolder extends Sprite
{
    cardsData: ArrayWrap<CardData, ICardData> | undefined;
    private _wid: number = 0;
    private _hei: number = 0;
    cards: Card[] = [];
    private betweenCardsMax: number = 10;
    private betweenCardsMin: number = -110;
    private onDragSelectedCardsOverAnotherBindthis: (e: Event) => void;
    private onDragSelectedStacksOverCardBindthis: (e: Event) => void;
    private onHandTakeCardsBindThis: (e: Event) => void;
    private onHandHighlightedBindThis: (e: Event) => void;
    private onHandUnhighlightedBindThis: (e: Event) => void;
    private onSortCardsBindThis: (e: Event) => void;
    private tempCardsCreated: boolean = false;

    constructor() {
        super();

        this.onDragSelectedCardsOverAnotherBindthis = this.onDragSelectedCardsOverAnother.bind(this);
        this.onDragSelectedStacksOverCardBindthis = this.onDragSelectedStacksOverCard.bind(this);
        this.onHandTakeCardsBindThis = this.onHandTakeCards.bind(this);
        this.onHandHighlightedBindThis = this.onHandHighlighted.bind(this);
        this.onHandUnhighlightedBindThis = this.onHandUnhighlighted.bind(this);
        this.onSortCardsBindThis = this.onSortCards.bind(this);

        addEventListener(HandEvents.DRAG_SELECTED_CARDS_OVER_ANOTHER_CARD, this.onDragSelectedCardsOverAnotherBindthis);
        addEventListener(HandEvents.DRAG_SELECTED_STACKS_OVER_CARD, this.onDragSelectedStacksOverCardBindthis);
        addEventListener(CardEvents.ON_HAND_TAKE_VIRTUAL_CARDS, this.onHandTakeCardsBindThis);
        addEventListener(HandEvents.HIGHLIGHT, this.onHandHighlightedBindThis);
        addEventListener(HandEvents.UNHIGHLIGHT, this.onHandUnhighlightedBindThis);
        addEventListener(HandEvents.SORT_CARDS, this.onSortCardsBindThis);
    }

    getPlaceIndex(right: boolean): number {
        if (!this.cards.length) {
            return 0;
        }
        return right ? this.cards.length : 0;
    }

    async initialize(cardsData: ArrayWrap<CardData, ICardData>): Promise<void> {
        this.clearListeners();

        this.cardsData = cardsData;

        this.cardsData.addListener(ArrayWrapEvent.ELEMENT_ADDED, this.onCardAdded, this);
        this.cardsData.addListener(ArrayWrapEvent.ELEMENT_REMOVED, this.onCardRemoved, this);
    }

    onDragSelectedCardsOverAnother(e: Event): void {
        let data: {overCard: Card} = (e as MessageEvent).data;
        let overCardIndex: number = this.cardsData!.indexOf(data.overCard.activeData);

        this.tempShowDraggingCardsAtIndex(overCardIndex);

    }

    async onDragSelectedStacksOverCard(e: Event): Promise<void> {
        let data: {overCard: Card} = (e as MessageEvent).data;
        let overCardIndex: number = this.cardsData!.indexOf(data.overCard.activeData);
        this.tempShowDraggingStacksAtIndex(overCardIndex);

    }

    async drawCards(): Promise<void> {
        for (let cardData of this.cardsData!.classes) {
            await this.addCard(cardData, this.cards.length);
        }
    }

    async addCard(cardData: CardData, index: number, sourceStack: Stack | undefined = undefined): Promise<void> {
        let card: Card = new Card(cardData);
        card.sourceStack = sourceStack;
        await card.initialize();
        this.cards.splice(index, 0, card);
        this.addChild(card);
        if (cardData.id == -1) {
            card.standUp(true);
        }
    }

    clearListeners(): void {
        this.cardsData && this.cardsData.removeListener(ArrayWrapEvent.ELEMENT_ADDED, this.onCardAdded, this);
        this.cardsData && this.cardsData.removeListener(ArrayWrapEvent.ELEMENT_REMOVED, this.onCardRemoved, this);
    }

    destroy(): void {
        this.clearListeners();
        this.clear();
        delete this.cards;
        delete this.cardsData;

        removeEventListener(HandEvents.DRAG_SELECTED_CARDS_OVER_ANOTHER_CARD, this.onDragSelectedCardsOverAnotherBindthis);
        removeEventListener(HandEvents.DRAG_SELECTED_STACKS_OVER_CARD, this.onDragSelectedStacksOverCardBindthis);
        removeEventListener(CardEvents.ON_HAND_TAKE_VIRTUAL_CARDS, this.onHandTakeCardsBindThis);
        removeEventListener(HandEvents.HIGHLIGHT, this.onHandHighlightedBindThis);
        removeEventListener(HandEvents.UNHIGHLIGHT, this.onHandUnhighlightedBindThis);
        removeEventListener(HandEvents.SORT_CARDS, this.onSortCardsBindThis);

        delete this.onDragSelectedCardsOverAnotherBindthis;
        delete this.onDragSelectedStacksOverCardBindthis;
        delete this.onHandTakeCardsBindThis;
        delete this.onHandHighlightedBindThis;
        delete this.onHandUnhighlightedBindThis;
        delete this.onSortCardsBindThis;

        super.destroy({children: true});
    }

    placeCards(): number {
        if (!this.cards.length) {
            return 0;
        }
        this.cards.forEach(card => {
            CardSelector.selected(card) || card.scaleToHeight(this._hei);
        });
        let totalWidth: number = 0;
        this.cards.forEach(card => {
            totalWidth += card.wid;
        });

        let shortBetweenCardsDirty: number = -(totalWidth - (this._wid)) / (this.cards.length - 1);
        let shortBetweenCards: number = PixiUtil.clamp(this.betweenCardsMin, shortBetweenCardsDirty, this.betweenCardsMax);
        totalWidth = 0;
        this.cards.forEach((card: Card, index: number) => {
            totalWidth += card.wid;
            if (index < this.cards.length - 1) {
                totalWidth += shortBetweenCards;
            }
        });

        let cardsStartX: number = Math.max((this._wid - totalWidth) / 2, 0);
        let currentCardOffset: number = 0;
        this.cards.forEach(card => {
            card.goX = cardsStartX + currentCardOffset;
            currentCardOffset += card.wid + shortBetweenCards;
        });
        return totalWidth;
    }

    setSizes(width: number, height: number): number {
        this._wid = width;
        this._hei = height;
        return this.placeCards();
    }

    clear(): void {
        let card: Card;
        while (this.cards.length) {
            card = this.cards.shift()!;
            this.removeChild(card);
            card.destroy();
        }
    }

    tempShowDraggingCardsAtIndex(overCardIndex: number): void {
        let placeAtZero: number = 0;

        //создаём временные карты
        let tempCardsData: CardData[] = this.cardsData!.classes.concat();

        //убираем родителей таскаемых карт из врменных карт
        tempCardsData = tempCardsData.filter(cardData => !CardSelector.selectedCards.find(selectedCard => selectedCard.sourceCard!.activeData === cardData));

        //сортируем таскаемые карты по возрастанию индекса при таскании
        CardSelector.selectedCards.sort((card1: Card, card2: Card) => CardSelector.dragIndexByTempCard.get(card1)! - CardSelector.dragIndexByTempCard.get(card2)!);

        //каждую таскаемую карту кладём во временные карты в соответствии с индексом при таскании
        CardSelector.selectedCards.forEach(selectedCard => {
            let wantsToPlace: number = overCardIndex + CardSelector.dragIndexByTempCardNormalized.get(selectedCard)!;
            let wantsToPlaceAtZero: number = Math.max(wantsToPlace, placeAtZero);
            if (wantsToPlace != wantsToPlaceAtZero) {
                placeAtZero++;
            }
            tempCardsData.splice(wantsToPlaceAtZero, 0, selectedCard.sourceCard!.activeData);
        });
        //приводим данные и собственный масив карт в соответствие с сортировкой временных карт
        tempCardsData.forEach((cardData, index) => {
            this.cardsData!.classes[index] = cardData;
            this.cardsData!.interfaces[index] = cardData.config!;
        });
        this.cards.sort((card1: Card, card2: Card) => this.cardsData!.indexOf(card1.activeData) - this.cardsData!.indexOf(card2.activeData));
        //раскладываем карты в соответствие с новым порядком
        this.placeCards();
        this.applyCardsZOrder();
    }

    async tempShowDraggingStacksAtIndex(overCardIndex: number): Promise<void> {
        await this.tryCreateTempDraggingCards(overCardIndex);
        let draggingCards: CardData[] = this.cardsData!.classes.filter(cardData => cardData.config!.id == -1);
        let placeAtZero: number = 0;

        //создаём временные карты
        let tempCardsData: CardData[] = this.cardsData!.classes.concat();

        //убираем родителей таскаемых карт из врменных карт
        tempCardsData = tempCardsData.filter(cardData => cardData.id != -1);

        //сортируем таскаемые карты по возрастанию индекса при таскании
        //CardSelector.selectedCards.sort((card1: Card, card2: Card) => CardSelector.dragIndexByTempCard.get(card1)! - CardSelector.dragIndexByTempCard.get(card2)!);

        //каждую таскаемую карту кладём во временные карты в соответствии с индексом при таскании
        draggingCards.forEach((cardData: CardData, index: number) => {
            let wantsToPlace: number = overCardIndex + index!;
            let wantsToPlaceAtZero: number = Math.max(wantsToPlace, placeAtZero);
            if (wantsToPlace != wantsToPlaceAtZero) {
                placeAtZero++;
            }
            tempCardsData.splice(wantsToPlaceAtZero, 0, cardData);
        });
        //приводим данные и собственный масив карт в соответствие с сортировкой временных карт
        tempCardsData.forEach((cardData, index) => {
            this.cardsData!.classes[index] = cardData;
            this.cardsData!.interfaces[index] = cardData.config!;
        });
        this.cards.sort((card1: Card, card2: Card) => this.cardsData!.indexOf(card1.activeData) - this.cardsData!.indexOf(card2.activeData));
        //раскладываем карты в соответствие с новым порядком
        this.placeCards();
        this.applyCardsZOrder();
    }

    makeTempCardsNotInteractive(): void {
        this.cards.filter(card => card.activeData.id == -1).forEach(card => {
            card.interactive = card.interactiveChildren = false;
        });
    }

    private onHandTakeCards(e: Event): void {
        let data: {cards: Card[]} = (e as MessageEvent).data;
        data.cards.forEach(card => {
            card.x += this.x;
        });
    }

    private async onCardAdded(data: ArrayWrapEventData<CardData>): Promise<void> {
        await this.addCard(data.value, data.index);
        dispatchEvent(new MessageEvent(CardsHolderEvents.CARDS_CHANGED, {data: {}}));
        data.value.id != -1 && this.removeTempDraggingCards();
    }

    private async onCardRemoved(data: ArrayWrapEventData<CardData>): Promise<void> {
        this.removeCard(data.value);
        dispatchEvent(new MessageEvent(CardsHolderEvents.CARDS_CHANGED, {data: {}}));
    }

    private removeCard(cardData: CardData) {
        let card: Card = this.cards.find(card => card.activeData === cardData)!;
        this.cards.splice(this.cards.indexOf(card), 1);
        this.removeChild(card);
        card.destroy();
    }

    private onHandHighlighted(e: Event): void {
        e;
        this.trySetSelectedStacksShown(false);
    }

    private onHandUnhighlighted(e: Event): void {
        e;
        this.removeTempDraggingCards();
        this.trySetSelectedStacksShown(true);
    }

    private onSortCards(e: Event): void {
        let card: Card = (e as MessageEvent).data.card;
        this.applyCardsZOrder(card);
    }

    private trySetSelectedStacksShown(value: boolean): void {
        if (DynamicData.dragSomething && StackSelector.selectedStacks.length) {
            StackSelector.selectedStacks.forEach(stack => stack.visible = value);
        }
    }

    private removeTempDraggingCards() {
        let draggingCards: CardData[] = this.cardsData!.classes.filter(cardData => cardData.config!.id == -1);
        let cardData: CardData;
        while (draggingCards.length) {
            cardData = draggingCards.shift()!;
            this.cardsData!.splice(this.cardsData!.indexOf(cardData), 1);
        }
        ActiveData.dispatchEventsPool();
        this.tempCardsCreated = false;
    }

    private async tryCreateTempDraggingCards(overCardIndex: number): Promise<void> {
        if (!this.tempCardsCreated && DynamicData.dragSomething && StackSelector.selectedStacks.length) {
            this.tempCardsCreated = true;
            let stack: Stack;
            let iCardData: ICardData;
            for (let selectedStackIndex: number = 0; selectedStackIndex < StackSelector.selectedStacks.length; selectedStackIndex++) {
                stack = StackSelector.selectedStacks[selectedStackIndex];

                for (let iCardDataIndex: number = 0; iCardDataIndex < stack.activeData.cards.interfaces.length; iCardDataIndex++) {
                    iCardData = stack.activeData.cards.interfaces[iCardDataIndex];
                    let config: ICardData = {
                        backfacePaint: iCardData.backfacePaint,
                        backface_src: iCardData.backface_src,
                        face_src: iCardData.face_src,
                        flipped: iCardData.flipped,
                        height: iCardData.height,
                        id: -1,
                        metaType: iCardData.metaType,
                        paint: iCardData.paint,
                        selected: iCardData.selected,
                        title: iCardData.title,
                        type: iCardData.type,
                        width: iCardData.width,
                        zIndex: iCardData.zIndex
                    };

                    this.cardsData!.push(config);
                    ActiveData.hideEvents();
                    ActiveData.dispatchEventsPool();
                    ActiveData.showEvents();
                    await this.addCard(this.cardsData!.classes.find(cardData => cardData.config == config)!, overCardIndex++, stack);
                }
            }
        }
    }

    private applyCardsZOrder(card: Card | null = null) {
        if (card && !card.dragged) {
            let highlightedIndex: number = this.cards.indexOf(card!);
            this.children.sort((card1: DisplayObject, card2: DisplayObject) => {
                return Math.abs(highlightedIndex - this.cards.indexOf(card2 as Card)) - Math.abs(highlightedIndex - this.cards.indexOf(card1 as Card));
            });
        } else {
            this.children.sort((card1: DisplayObject, card2: DisplayObject) => {
                return this.cards.indexOf(card1 as Card) - this.cards.indexOf(card2 as Card);
            });
        }
    }
}
